import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, finalize } from 'rxjs';
import { shopCrossSelling, shopForbidden, shopPendingApproval } from 'src/app/core/constants/routes';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { accountHasAddon } from 'src/app/core/utils/global-utils';
import { environment } from 'src/environments/environment';

/** Service used to hancle shop redirects */
@Injectable({
  providedIn: 'root',
})
export class ShopRedirectService {
  private _isCreatingRedirectToken: boolean = false;

  /** True if the service is currently creating the redirect token */
  get isCreatingRedirectToken(): boolean {
    return this._isCreatingRedirectToken;
  }

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private http: HttpClient,
    private permissionsService: PermissionsService,
  ) {}

  xchangeClicked(afterShopRedirect?: () => void) {
    const doesCompanyHaveShop = accountHasAddon('shop', this.userDataService.company?.serviceAddons ?? []);
    const isUserPendingApproval = this.userDataService.user?.pendingApprovalAddons?.includes('shop') ?? false;
    const doesUserHaveAccess = this.permissionsService.hasPermission('shop', 'Shop', 'view');
    this.handleShopModuleAccess(doesCompanyHaveShop, isUserPendingApproval, doesUserHaveAccess, afterShopRedirect);
  }

  /**
   * Determines if the user can navigate to the shop module, otherwise redirects the user to the appropriate component based on the access flags
   * @param doesCompanyHaveShop True if the company has the shop service addon
   * @param isUserPendingApproval True if the user is pending approval for the shop module
   * @param doesUserHaveAccess True if the user has Shop view permission
   * @param afterShopRedirect The callback to be executed after the user is redirected to the shop (optional)
   */
  private handleShopModuleAccess(
    doesCompanyHaveShop: boolean,
    isUserPendingApproval: boolean,
    doesUserHaveAccess: boolean,
    afterShopRedirect?: () => void,
  ) {
    if (!doesCompanyHaveShop) {
      this.router.navigateByUrl(shopCrossSelling);
    } else if (isUserPendingApproval) {
      this.router.navigateByUrl(shopPendingApproval);
    } else if (doesUserHaveAccess) {
      this.redirectToShop();
      afterShopRedirect?.();
    } else {
      this.router.navigateByUrl(shopForbidden);
    }
  }

  private redirectToShop() {
    this._isCreatingRedirectToken = true;
    this.createRedirectToken()
      .pipe(finalize(() => (this._isCreatingRedirectToken = false)))
      .subscribe((tokenResult) => {
        window.open(
          `${environment.redirects['xchange']}${environment.genXchangeToken ? `?redirect-token=${tokenResult.token}` : ''}`,
          '_blank',
        );
      });
  }

  private createRedirectToken(): Observable<{ token: string }> {
    const apiUri = `${environment.restUrl}/v1/integrations/shop/redirect-token`;

    return this.http.post<{ token: string }>(apiUri, {
      responseType: 'json',
    });
  }
}
